import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { EvaluationCategoryData } from './Evaluation.service';

let recognizer = null;

export const startSpeechRecognition = async (onRecognizing, onRecognized, onError, onStopped) => {
  try {
    // Get token from our backend
    const tokenData = await EvaluationCategoryData.getCognitiveServiceToken();

    let speechConfig;

    if (tokenData.useKey && tokenData.endpoint) {
      // Custom domain endpoint (e.g., Azure AI Foundry / AI Services resource)
      // Must use fromEndpoint with the full WSS speech endpoint URL + subscription key
      const cleanEndpoint = tokenData.endpoint.replace(/\/$/, '');
      const endpointUrl = new URL(`${cleanEndpoint}/stt/speech/recognition/conversation/cognitiveservices/v1`);
      endpointUrl.searchParams.set('language', 'en-US');
      speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpointUrl, tokenData.subscriptionKey);
    } else if (tokenData.endpoint) {
      // Custom endpoint with token auth
      const endpointUrl = new URL(tokenData.endpoint.replace(/\/$/, ''));
      speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpointUrl, '');
      speechConfig.authorizationToken = tokenData.token;
    } else {
      // Standard regional endpoint with token
      speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        tokenData.token,
        tokenData.region
      );
    }

    speechConfig.speechRecognitionLanguage = 'en-US';

    // Enable detailed output for better accuracy
    speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;

    // Enable automatic punctuation (on by default for en-US, but set explicitly)
    speechConfig.setProfanity(SpeechSDK.ProfanityOption.Masked);

    // Enable disfluency removal (removes "um", "uh", etc.)
    speechConfig.setProperty("speechContext-PhraseDetection.DisfluencyRemoval", "true");

    // Enable richer punctuation and formatting via service properties
    speechConfig.setServiceProperty("punctuation", "explicit", SpeechSDK.ServicePropertyChannel.UriQueryParameter);
    speechConfig.enableDictation();

    // ── Noisy Environment Optimizations (basketball court / live game) ──

    // Enable Microsoft's enhanced noise suppression (best for high-noise environments)
    speechConfig.setProperty("AudioProcessing_EnableNoiseSuppression", "true");
    speechConfig.setProperty("AudioProcessing_NoiseSuppressionLevel", "High");

    // Enable automatic gain control to normalize volume spikes (buzzers, whistles, crowd)
    speechConfig.setProperty("AudioProcessing_EnableAutomaticGainControl", "true");

    // Increase silence timeout so short pauses during game action don't stop recognition
    // segmentationSilenceTimeoutMs: how long silence before a phrase is considered complete
    speechConfig.setProperty(
      SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs,
      "3000"
    );

    // Increase initial silence timeout (wait longer for speech before giving up)
    speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
      "30000"
    );

    // Increase end silence timeout so recognition doesn't cut off mid-sentence
    speechConfig.setProperty(
      SpeechSDK.PropertyId.SpeechServiceConnection_EndSilenceTimeoutMs,
      "5000"
    );

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

    // Add domain-specific phrases to improve recognition accuracy in noisy environment
    const phraseListGrammar = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
    const domainPhrases = [
      // Basketball terms
      "foul", "personal foul", "technical foul", "flagrant foul", "offensive foul",
      "traveling", "double dribble", "shot clock violation", "backcourt violation",
      "out of bounds", "goaltending", "three-second violation", "five-second violation",
      "eight-second violation", "24-second violation", "lane violation",
      "jump ball", "held ball", "loose ball", "dead ball",
      "fast break", "transition", "half court", "full court",
      "free throw", "three-pointer", "two-pointer", "layup", "dunk",
      "block", "charge", "screen", "pick and roll",
      // Referee evaluation terms
      "mechanics", "positioning", "court presence", "communication",
      "signals", "presentation", "interactions", "professionalism",
      "game awareness", "PCA coverage", "transition coverage",
      "primary coverage area", "lead official", "trail official", "slot official",
      "crew chief", "referee", "umpire",
      // Rating language
      "needs improvement", "below expectations", "meets expectations",
      "above expectations", "exceptional", "outstanding", "satisfactory",
      // Game context
      "first quarter", "second quarter", "third quarter", "fourth quarter",
      "overtime", "halftime", "timeout", "instant replay", "challenge",
      "NBA", "WNBA", "G League",
    ];
    domainPhrases.forEach((phrase) => phraseListGrammar.addPhrase(phrase));

    // Fires while speech is being recognized (interim results)
    recognizer.recognizing = (s, e) => {
      if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech) {
        onRecognizing(e.result.text);
      }
    };

    // Fires when a complete phrase is recognized
    recognizer.recognized = (s, e) => {
      if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
        let text = e.result.text;
        // Capitalize first letter if not already
        if (text && text.length > 0) {
          text = text.charAt(0).toUpperCase() + text.slice(1);
        }
        // Ensure sentence ends with punctuation
        if (text && !/[.!?]$/.test(text.trim())) {
          text = text.trim() + '.';
        }
        onRecognized(text);
      } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
        // No speech detected
      }
    };

    // Fires on errors
    recognizer.canceled = (s, e) => {
      if (e.reason === SpeechSDK.CancellationReason.Error) {
        onError(`Speech recognition error: ${e.errorDetails}`);
      }
      stopSpeechRecognition();
      onStopped();
    };

    // Fires when session stops
    recognizer.sessionStopped = () => {
      stopSpeechRecognition();
      onStopped();
    };

    // Start continuous recognition
    recognizer.startContinuousRecognitionAsync(
      () => {
        console.log('Speech recognition started');
      },
      (err) => {
        onError(`Failed to start speech recognition: ${err}`);
        onStopped();
      }
    );

    return true;
  } catch (error) {
    onError(`Failed to initialize speech recognition: ${error.message}`);
    onStopped();
    return false;
  }
};

export const stopSpeechRecognition = () => {
  if (recognizer) {
    recognizer.stopContinuousRecognitionAsync(
      () => {
        recognizer.close();
        recognizer = null;
        console.log('Speech recognition stopped');
      },
      (err) => {
        console.error('Error stopping recognition:', err);
        recognizer = null;
      }
    );
  }
};
